/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  SkillAttainmentProgress_AttainmentStatus as AttainmentStatus,
  Certificates_ProductType as CertificatesProductType,
} from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Accordion, AccordionHeader, AccordionPanel, Button, Grid, Skeleton, Typography2 } from '@coursera/cds-core';
import { ReloadIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type { Item } from 'bundles/learner-progress/types/Item';
import { type Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import SkillAreasProgressBarWithThreshold from 'bundles/skills-common/shared/verified-skills/components/SkillAreasProgressBarWithThreshold';
import SkillProgressRetryAssessmentControls from 'bundles/skills-common/shared/verified-skills/components/SkillProgressRetryAssessmentControls';
import SkillProgressTakeAssessmentControls from 'bundles/skills-common/shared/verified-skills/components/SkillProgressTakeAssessmentControls';
import type { RenderRetryActionButton } from 'bundles/skills-common/shared/verified-skills/components/SkillsDialog';
import { useSkillAreasThresholdRequestTracker } from 'bundles/skills-common/shared/verified-skills/context/SkillAreasThresholdRequestTracker';
import { useGetVerifiedSkillCourseCertificate } from 'bundles/skills-common/shared/verified-skills/data/certificate/useGetVerifiedSkillCourseCertificate';
import type { VerifiedSkillsEnterpriseBadgeCollectionFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';
import type { VerifiedSkillsEnterpriseProgramsV1Fragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import type {
  SkillAttainmentSkillAreaAttainmentFragment,
  SkillAttainmentSkillExpressionAttainmentFragment,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type {
  ProgramCurriculumCollectionsV1ProgramCourseWithStateFragment,
  SkillPathwayCurriculumProductsFragment,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillPathwayCurriculumProductsQuery';
import useGetSkillAssessmentItems from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetSkillAssessmentItems';
import type { TrackingData } from 'bundles/skills-common/shared/verified-skills/types/TrackingData';
import { isVerifiedSkillCourseCompleted } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

import _t from 'i18n!nls/skills-common';

type Props = {
  skillAreaAttainment: SkillAttainmentSkillAreaAttainmentFragment;
  focusedSkillAreaId?: string;
  skillPathCurriculumProducts?: (SkillPathwayCurriculumProductsFragment | null)[];
  program?: VerifiedSkillsEnterpriseProgramsV1Fragment | null;
  enrolledBadgeCollection?: VerifiedSkillsEnterpriseBadgeCollectionFragment | null;
  skillPath?: Track;
  item?: Item;
  trackingData?: TrackingData;
  renderRetryActionButton?: RenderRetryActionButton;
  enrollmentProps?: {
    programId: string;
    pathwayCollectionId?: string;
    programSlug?: string;
    thirdPartyOrganizationSlug?: string;
  };
  skillAreasWithUserDataLoading?: boolean;
  handleDialogOnClose?: () => void;
};

const styles = {
  accordionOverrides: css`
    .cds-AccordionHeader-freeContent {
      padding-left: 0 !important;
    }

    .cds-AccordionHeader-labelGroup,
    .cds-AccordionHeader-labelGroup > div:first-child {
      width: 100% !important;
    }

    .cds-AccordionHeader-button,
    .cds-AccordionRoot-focusContainer {
      padding-left: 0 !important;
    }
  `,
  columnContainer: css`
    padding-left: var(--cds-spacing-50);
  `,
  column: css`
    padding-left: var(--cds-spacing-200);
  `,
  list: css`
    padding-left: var(--cds-spacing-200);
    color: var(--cds-color-neutral-primary-weak);
  `,
  listItem: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  skeletonContainer: css`
    padding-left: var(--cds-spacing-200);
  `,
  skeletonRow: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-200);
  `,
  skeletonActionRow: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--cds-spacing-200);
  `,
};

const AccordionPanelSkeleton = () => (
  <Grid container css={styles.skeletonContainer}>
    <Grid item xs={12}>
      <Skeleton variant="text" width={120} css={styles.listItem} />
    </Grid>
    <Grid item md={6} xs={12}>
      <div css={styles.skeletonRow}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="75%" />
      </div>
    </Grid>
    <Grid item md={6} xs={12}>
      <div css={styles.skeletonRow}>
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="text" width="65%" />
        <Skeleton variant="text" width="78%" />
      </div>
    </Grid>
    <Grid item xs={12}>
      <div css={styles.skeletonActionRow}>
        <Skeleton variant="text" width={180} />
        <Skeleton variant="rect" width={140} height={36} />
      </div>
    </Grid>
  </Grid>
);

const SkillExpressionListAll = ({
  skillExpressionsTotal,
  isAllMastered,
}: {
  skillExpressionsTotal: SkillAttainmentSkillExpressionAttainmentFragment[];
  isAllMastered: boolean;
}) => {
  return (
    <Grid container css={styles.columnContainer}>
      <Grid item xs={12} css={styles.column}>
        <Typography2 variant="subtitleSmall" component="p" css={styles.listItem}>
          {isAllMastered ? (
            <FormattedMessage
              message={_t('Mastered {numberOfSkillsMastered, plural, =1 {skill} other {skills}}')}
              numberOfSkillsMastered={skillExpressionsTotal.length}
            />
          ) : (
            <FormattedMessage
              message={_t('{numberOfSkillsToMaster, plural, =1 {Skill} other {Skills}} to master')}
              numberOfSkillsToMaster={skillExpressionsTotal.length}
            />
          )}
        </Typography2>
      </Grid>
      <Grid item md={6} xs={12} css={styles.column}>
        <ul css={styles.list}>
          {skillExpressionsTotal.slice(0, Math.ceil(skillExpressionsTotal.length / 2)).map((skillExpression) => (
            <li key={skillExpression.skillExpression.id}>
              <Typography2 variant="bodySecondary" component="p" css={styles.listItem}>
                {skillExpression.skillExpression.name}
              </Typography2>
            </li>
          ))}
        </ul>
      </Grid>
      <Grid item md={6} xs={12} css={styles.column}>
        <ul css={styles.list}>
          {skillExpressionsTotal.slice(Math.ceil(skillExpressionsTotal.length / 2)).map((skillExpression) => (
            <li key={skillExpression.skillExpression.id}>
              <Typography2 variant="bodySecondary" component="p" css={styles.listItem}>
                {skillExpression.skillExpression.name}
              </Typography2>
            </li>
          ))}
        </ul>
      </Grid>
    </Grid>
  );
};

const SkillExpressionListMixed = ({
  skillExpressionsMastered,
  skillExpressionsNotMastered,
}: {
  skillExpressionsMastered: SkillAttainmentSkillExpressionAttainmentFragment[];
  skillExpressionsNotMastered: SkillAttainmentSkillExpressionAttainmentFragment[];
}) => {
  return (
    <Grid container css={styles.columnContainer}>
      <Grid item md={6} xs={12} css={styles.column}>
        <Typography2 variant="subtitleSmall" component="p" css={styles.listItem}>
          <FormattedMessage
            message={_t('Mastered ({numberOfSkillsMastered})')}
            numberOfSkillsMastered={skillExpressionsMastered.length}
          />
        </Typography2>
        <ul css={styles.list}>
          {skillExpressionsMastered.map((skillExpression) => (
            <li key={skillExpression.skillExpression.id}>
              <Typography2 variant="bodySecondary" component="p" css={styles.listItem}>
                {skillExpression.skillExpression.name}
              </Typography2>
            </li>
          ))}
        </ul>
      </Grid>
      <Grid item md={6} xs={12} css={styles.column}>
        <Typography2 variant="subtitleSmall" component="p" css={styles.listItem}>
          <FormattedMessage
            message={_t('Not yet mastered ({numberOfSkillsNotMastered})')}
            numberOfSkillsNotMastered={skillExpressionsNotMastered.length}
          />
        </Typography2>
        <ul css={styles.list}>
          {skillExpressionsNotMastered.map((skillExpression) => (
            <li key={skillExpression.skillExpression.id}>
              <Typography2 variant="bodySecondary" component="p" css={styles.listItem}>
                {skillExpression.skillExpression.name}
              </Typography2>
            </li>
          ))}
        </ul>
      </Grid>
    </Grid>
  );
};

const CurrentAssessmentActionControls = ({
  isSkillAreaMastered,
  itemName,
  timeCommitment,
  actionButton,
  handleDialogOnClose,
}: {
  isSkillAreaMastered: boolean;
  itemName: string;
  timeCommitment?: number;
  actionButton?: React.ReactNode;
  handleDialogOnClose?: () => void;
}) => {
  // for the current assessment (accessing the skills dialog from current assessment result page)
  // if the learner already attained all skills, we can just close the dialog and allow them to "review"
  // all other states is controlled by the incoming actionButton from assessment context
  return (
    <SkillProgressRetryAssessmentControls
      courseName={itemName}
      timeCommitment={timeCommitment}
      actionButton={
        isSkillAreaMastered || !actionButton ? (
          <Button variant="secondary" size="small" onClick={handleDialogOnClose}>
            {_t('Review assessment')}
          </Button>
        ) : (
          actionButton
        )
      }
    />
  );
};

const SkillProgressDetailedListItem = ({
  skillAreaAttainment,
  focusedSkillAreaId,
  skillPathCurriculumProducts,
  program,
  enrolledBadgeCollection,
  skillPath,
  item,
  trackingData,
  renderRetryActionButton,
  enrollmentProps,
  skillAreasWithUserDataLoading = false,
  handleDialogOnClose,
}: Props) => {
  const track = useTracker();

  const { showProgressBars } = useSkillAreasThresholdRequestTracker();

  const handleAccordionChange = (_event: React.ChangeEvent<unknown>, expanded: boolean) => {
    track(expanded ? 'expand_accordion' : 'collapse_accordion', {
      accordion: {
        name: 'verified_skills_progress_dialog_view_skill_details',
      },
    });
  };

  const { targetAssessment, loading: assessmentLoading } = useGetSkillAssessmentItems({
    courseId: skillAreaAttainment.skillArea.course.id,
    courseSlug: skillAreaAttainment.skillArea.course.slug,
    skip: !skillAreaAttainment.skillArea.course.id,
  });

  const { certificateData, loading: courseCertificateLoading } = useGetVerifiedSkillCourseCertificate({
    productItemId: skillAreaAttainment.skillArea.course.id,
    productType: CertificatesProductType.Course,
    shouldSkipQuery: false,
  });

  const isCourseCompleted = isVerifiedSkillCourseCompleted(certificateData);

  if (!skillAreaAttainment?.skillArea?.id) {
    return null;
  }

  const skillExpressionsAttainmentTotal = skillAreaAttainment.skillExpressionsAttainment || [];
  const skillExpressionsMastered = skillExpressionsAttainmentTotal.filter(
    (skillExpressionAttainment) => skillExpressionAttainment.attainmentStatus === AttainmentStatus.FullAttainment
  );
  const skillExpressionsNotMastered = skillExpressionsAttainmentTotal.filter(
    (skillExpressionAttainment) => skillExpressionAttainment.attainmentStatus === AttainmentStatus.NoAttainment
  );
  const assessmentHasSomeSkillsAttained = skillExpressionsMastered.length > 0;
  const assessmentHasAllSkillsAttained =
    skillExpressionsMastered.length === skillExpressionsAttainmentTotal.length &&
    skillExpressionsAttainmentTotal.length > 0;
  const isCurrentSkillArea = skillAreaAttainment?.skillArea?.id === focusedSkillAreaId;

  // if current assessment is the same as target assessment, it means the current assessment has not be passed or all assessments have been completed
  const isCurrentAssessmentTheSameAsTargetAssessment = item?.id === targetAssessment?.itemId;

  // if the current skill area is the focused skill area and the current assessment is not the same as the target assessment, it means the learner needs to take the next assessment in the current skill area
  const showControlForNextAssessmentInCurrentSkillArea =
    isCurrentSkillArea && !isCurrentAssessmentTheSameAsTargetAssessment;

  const getAssessmentButtonConfig = () => {
    // if the current assessment has all skills attained or is passed or completed, it means the skill area has either been mastered or threshold has been reached
    if (assessmentHasAllSkillsAttained || targetAssessment?.isPassedOrCompleted) {
      return {
        text: _t('Review assessment'),
        icon: undefined,
      };
    }

    if (showControlForNextAssessmentInCurrentSkillArea) {
      return {
        text: _t('Take assessment'),
        icon: undefined,
      };
    }

    if (assessmentHasSomeSkillsAttained) {
      return {
        text: _t('Retry assessment'),
        icon: <ReloadIcon />,
      };
    }

    return {
      text: _t('Take assessment'),
      icon: undefined,
    };
  };

  const buttonConfig = getAssessmentButtonConfig();

  // check if user is enrolled in this specific course
  const courseProduct = skillPathCurriculumProducts?.find(
    (product) =>
      (product?.productState as ProgramCurriculumCollectionsV1ProgramCourseWithStateFragment)?.productState
        ?.courseId === skillAreaAttainment.skillArea.course.id
  );

  // enrolledAt can be 0, so check explicitly for null and undefined
  const isEnrolledInThisCourse =
    (courseProduct?.productState as ProgramCurriculumCollectionsV1ProgramCourseWithStateFragment)?.productState
      ?.enrolledAt !== undefined &&
    (courseProduct?.productState as ProgramCurriculumCollectionsV1ProgramCourseWithStateFragment)?.productState
      ?.enrolledAt !== null;

  const isSkillAreaMastered = !!skillAreaAttainment?.areAllExpressionsAttained || isCourseCompleted;

  return (
    <React.Fragment key={skillAreaAttainment.skillArea.id}>
      <Accordion
        variant="silent"
        css={styles.accordionOverrides}
        defaultExpanded={isCurrentSkillArea}
        onChange={handleAccordionChange}
      >
        <AccordionHeader
          label=""
          eyebrowContent={
            <SkillAreasProgressBarWithThreshold
              total={skillExpressionsAttainmentTotal.length}
              score={skillExpressionsMastered.length}
              title={skillAreaAttainment.skillArea.name}
              id={skillAreaAttainment.skillArea.id}
              useBoldText={true}
              courseId={skillAreaAttainment.skillArea.course.id}
            />
          }
          labelProps={{ 'aria-label': skillAreaAttainment.skillArea.name }}
        />

        <AccordionPanel>
          {!showProgressBars ? (
            <AccordionPanelSkeleton />
          ) : (
            <React.Fragment>
              {skillExpressionsMastered.length > 0 && skillExpressionsNotMastered.length > 0 ? (
                <SkillExpressionListMixed
                  skillExpressionsMastered={skillExpressionsMastered}
                  skillExpressionsNotMastered={skillExpressionsNotMastered}
                />
              ) : (
                <SkillExpressionListAll
                  skillExpressionsTotal={skillExpressionsAttainmentTotal}
                  isAllMastered={skillExpressionsMastered.length === skillExpressionsAttainmentTotal.length}
                />
              )}
              {/* If the current skill area is the focused skill area and the current assessment is not completed, show the retry assessment controls */}
              {isCurrentSkillArea && isCurrentAssessmentTheSameAsTargetAssessment && item && (
                <CurrentAssessmentActionControls
                  isSkillAreaMastered={isSkillAreaMastered}
                  itemName={item.name}
                  timeCommitment={item.timeCommitment}
                  actionButton={renderRetryActionButton?.(trackingData)}
                  handleDialogOnClose={handleDialogOnClose}
                />
              )}
              {/* If the current skill area is not the focused skill area or the current assessment is not completed, show the take assessment controls */}
              {(!isCurrentSkillArea || showControlForNextAssessmentInCurrentSkillArea) && (
                <SkillProgressTakeAssessmentControls
                  displayName={
                    showControlForNextAssessmentInCurrentSkillArea
                      ? targetAssessment?.name ?? skillAreaAttainment.skillArea.course.name
                      : skillAreaAttainment.skillArea.course.name
                  }
                  timeCommitment={targetAssessment?.totalWorkDuration}
                  isEnrolledInPathway={!!enrolledBadgeCollection} // real pathway enrollment status, fallback to false
                  isEnrolledInCourse={isEnrolledInThisCourse} // real course enrollment status, fallback to false
                  isProgramMember={!!program || !!enrollmentProps?.programId} // real program membership or assume true if we have programId from enterprise context
                  courseId={skillAreaAttainment.skillArea.course.id}
                  courseSlug={skillAreaAttainment.skillArea.course.slug}
                  programId={enrollmentProps?.programId || program?.id || ''} // use enterprise context or verified-skills context
                  assessmentId={targetAssessment?.itemId}
                  redirectToAssessment={true}
                  pathwayCollectionId={enrollmentProps?.pathwayCollectionId}
                  pathwayId={skillPath?.id}
                  programSlug={enrollmentProps?.programSlug || program?.metadata?.slug}
                  thirdPartyOrganizationSlug={
                    enrollmentProps?.thirdPartyOrganizationSlug || program?.thirdPartyOrg?.slug
                  }
                  buttonText={buttonConfig.text}
                  buttonIcon={buttonConfig.icon}
                  buttonIconPosition="before"
                  trackingData={{
                    ...trackingData,
                    button: { name: 'verified_skills_progress_dialog_start_assessment' },
                  }}
                  disabled={skillAreasWithUserDataLoading || assessmentLoading || courseCertificateLoading}
                />
              )}
            </React.Fragment>
          )}
        </AccordionPanel>
      </Accordion>
    </React.Fragment>
  );
};

export default SkillProgressDetailedListItem;
